<template>
  <div class="personal-info-container">
    <transition name="view" mode="out-in">
      <c-loader v-if="loading" :size="isMobile ? 79 : 99" />

      <div v-else class="forms">
        <form-group
          ref="formGroup"
          class="form"
          :label-left="!isMobile"
          :schemas="forms"
          :data="data"
          :options="options"
          :disabled="submitting"
          :required-fields="requiredFields"
          @sync:personal="onSyncPersonal"
          @sync:birthStateId="onChangeState"
        />

        <form-group
          v-if="!isNotForeign"
          ref="formGroupForeign"
          class="form"
          :label-left="!isMobile"
          :schemas="formsForeign"
          :data="data"
          :options="options"
          :disabled="submitting"
          :required-fields="requiredFields"
          @sync:foreign="onSyncPersonalForeign"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import { getFileUrl } from '@modules/fileHelpers'
import { MediaQuery } from '@convenia/mixins'
import FormGroup from '@components/FormGroup'
import * as types from '@types'
import useSystemFields from '@convenia/employee-organisms/SystemFields/composables/useSystemFields'
import personalSchemas from '@convenia/employee-organisms/SystemFields/Personal/content/forms/personal'
import foreignSchemas from '@convenia/employee-organisms/SystemFields/Personal/content/forms/foreign'
import handicapSchemas from '@convenia/employee-organisms/SystemFields/Personal/content/forms/handicap'
import { mapPersonal } from '@convenia/employee-organisms/SystemFields/Personal/content/mappers/personal'
import { mapForeign } from '@convenia/employee-organisms/SystemFields/Personal/content/mappers/foreign'
import { mapHandicap } from '@convenia/employee-organisms/SystemFields/Personal/content/mappers/handicap'
import { isNotForeign } from '@convenia/employee-organisms/SystemFields/Personal/helpers/foreign'

export default {
  name: 'PersonalInfo',

  mixins: [ MediaQuery ],

  components: {
    FormGroup
  },

  setup () {
    const { proxy } = getCurrentInstance()
    const schemas = {
      personal: personalSchemas(proxy),
      foreign: foreignSchemas(),
      handicap: handicapSchemas(),
    }

    const {
      getFormValues: getPersonalValues,
      formSchema: personalFormSchema,
      requiredFields: personalRequiredFields
    } = useSystemFields({
      schema: computed(() => schemas.personal),
      systemFields: computed(() => proxy.systemFields?.personal || {}),
      customFields: computed(() => proxy.customFields?.personal || {}),
      persistentFields: [ 'gender' ]
    })

    const {
      getFormValues: getForeignValues,
      formSchema: foreignFormSchema,
      requiredFields: foreignRequiredFields
    } = useSystemFields({
      schema: computed(() => schemas.foreign),
      systemFields: computed(() => proxy.systemFields?.foreign || {}),
      customFields: computed(() => proxy.customFields?.foreign || {}),
    })

    const {
      getFormValues: getHandicapValues,
      formSchema: handicapFormSchema,
      requiredFields: handicapRequiredFields
    } = useSystemFields({
      schema: computed(() => schemas.handicap),
      systemFields: computed(() => proxy.systemFields?.handicap || {}),
      customFields: computed(() => proxy.customFields?.handicap || {}),
      persistentFields: [ 'hasDeficiency' ]
    })

    return {
      getPersonalValues,
      getForeignValues,
      getHandicapValues,
      personalFormSchema,
      personalRequiredFields,
      foreignFormSchema,
      foreignRequiredFields,
      handicapFormSchema,
      handicapRequiredFields,
    }
  },

  computed: {
    ...mapState({
      employeeId: state => state.admission.employeeUuid,
      personal: state => state.personalInfo.personal,
      systemFields: state => state.personalInfo.systemFields,
      foreign: state => state.personalInfo.foreign,
      handicap: state => state.personalInfo.handicap,
      cities: state => state.location.citiesList,
      customFields: state => state.personalInfo.customFields
    }),

    options () {
      const { residenceTime } = (this.foreignData || {})

      const undetermined = [ 1, 2, 3, 8 ]
      const determined = [ 4, 5, 2, 6, 7, 8 ]
      const entryConditions = this.systemFields?.foreign?.entryConditionId?.options || []

      const entryOptions = entryConditions?.reduce((acc, cur) => {
        if (residenceTime === 1 && undetermined.includes(cur.id))
          acc.push(cur)
        if (residenceTime === 2 && determined.includes(cur.id))
          acc.push(cur)
        return acc
      }, [])

      return {
        foreign: {
          entryConditionId: entryOptions
        },
        personal: {
          cities: this.cities,
        }
      }
    },

    handicapSchema () {
      const schema = this.handicapFormSchema || {}

      return Object.entries(schema)
        .reduce((acc, [ key, value ]) => ({
          ...acc,
          [key]: {
            ...value,
            isDisabled: formData => !formData.hasDeficiency && key !== 'hasDeficiency'
          }
        }), {})
    },

    hasDisabilityFields () {
      return Object.keys(this.handicapSchema).length >= 2
    },

    forms () {
      return {
        personal: {
          title: 'Dados Pessoais',
          fields: this.personalFormSchema,
          divider: this.hasDisabilityFields,
        },
        ...(this.hasDisabilityFields ? {
          handicap: {
            fields: this.handicapSchema,
            divider: false,
          }
        } : {})
      }
    },

    formsForeign () {
      return {
        foreign: {
          title: 'Estrangeiro',
          fields: this.foreignFormSchema
        }
      }
    },

    submitting: {
      get () { return this.$store.state.admission.submitting },
      set (value) { this.$store.commit(types.ADMISSION_SUBMITTING, value) }
    },

    requiredFields () {
      return [
        ...(this.personalRequiredFields || []),
        ...(this.foreignRequiredFields || []),
        ...(this.handicapRequiredFields || []),
      ]
    }
  },

  data: () => ({
    loading: true,
    data: {
      personal: {},
      foreign: {},
      handicap: {},
    },
    isNotForeign: true,
    foreignData: null
  }),

  mounted () {
    this.$root.$on('next', this.onSubmit)
    this.$root.$on('previous', this.onSave)
    this.init()
  },

  beforeDestroy () {
    this.$root.$off('next', this.onSubmit)
    this.$root.$off('previous', this.onSave)
  },

  methods: {
    ...mapMutations({
      savePersonal: types.EMPLOYEE_UPDATE_PERSONAL,
      saveForeign: types.EMPLOYEE_UPDATE_FOREIGN,
      saveHandicap: types.EMPLOYEE_UPDATE_HANDICAP,
    }),

    ...mapActions({
      getSystemFields: types.EMPLOYEE_PERSONAL_SYSTEM_FIELDS,
      getPersonal: types.EMPLOYEE_PERSONAL_INFO,
      getNationalities: types.EMPLOYEE_GET_NATIONALITIES,
      getHandicap: types.EMPLOYEE_GET_HANDICAP,
      getForeign: types.EMPLOYEE_GET_FOREIGN,
      getCities: types.LOCATION_CITIES,
      updatePersonal: types.EMPLOYEE_UPDATE_PERSONAL,
      updateEmployee: types.EMPLOYEE_UPDATE_EMPLOYEE,
      updateNationalities: types.EMPLOYEE_UPDATE_NATIONALITIES,
      updateForeign: types.EMPLOYEE_UPDATE_FOREIGN,
      updateHandicap: types.EMPLOYEE_UPDATE_HANDICAP,
      deleteHandicap: types.EMPLOYEE_DELETE_HANDICAP,
    }),

    async init () {
      await Promise.allSettled([
        this.getPersonal(),
        this.getNationalities(),
        this.getHandicap(),
        this.getForeign(),
      ])

      await this.getSystemFields()

      if (this.personal.birthState?.id)
        await this.getCities(this.personal.birthState?.id)

      const { employeeId } = this

      this.data = {
        personal: this.getPersonalValues(
          mapPersonal(this.personal, { employeeId, getFileUrl }),
          this.personal?.customFieldValues
        ),
        handicap: this.getHandicapValues(
          mapHandicap(this.handicap, { employeeId, getFileUrl }),
          this.handicap?.customFieldValues,
        ),
        foreign: this.getForeignValues(
          mapForeign(this.foreign, { employeeId, getFileUrl }),
          this.foreign?.customFieldValues,
        )
      }

      this.loading = false
    },

    async validate () {
      if (this.$refs.formGroup && !await this.$refs.formGroup.validate())
        return false

      if (this.$refs.formGroupForeign && !await this.$refs.formGroupForeign?.validate())
        return false

      return true
    },

    resetForeign () {
      const values = Object.keys(this.foreign || {})
        .reduce((acc, key) => ({ ...acc, [key]: null }), {})

      this.saveForeign(values)
    },

    async onSubmit () {
      if (!await this.validate()) return

      const { personal, handicap } = this.$refs.formGroup?.formsData || {}
      const { foreign } = this.$refs.formGroupForeign?.formsData || {}

      this.submitting = true

      const requests = [
        this.updatePersonal(personal),
        this.updateEmployee(personal),
        this.updateNationalities(personal),
      ]

      if (!this.isNotForeign)
        requests.push(this.updateForeign(foreign))
      else
        this.resetForeign()

      if (handicap?.hasDeficiency)
        requests.push(this.updateHandicap(handicap))
      else if (this.hasDisabilityFields && this.handicap?.id)
        requests.push(this.deleteHandicap())

      const results = await Promise.allSettled(requests)

      this.submitting = false

      const hasErrors = results.some(({ value: [ err ] = [] } = {}) => !!err)

      if (hasErrors)
        return this.$feedback.add({
          type: 'error',
          message: 'Erro',
          highlighted: 'ao salvar Dados Pessoais. Tente novamente.'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Informações pessoais',
        highlighted: 'atualizadas com sucesso'
      })

      this.$emit('next')
    },

    async onSave () {
      if (!this.$refs.formGroup) return

      this.$emit('previous')
    },

    onChangeState () {
      const { personal } = this.data

      this.data.personal = {
        ...personal,
        birthCityId: undefined
      }
    },

    onSyncPersonal (personal) {
      this.isNotForeign = isNotForeign(personal)
    },

    onSyncPersonalForeign (foreign) {
      this.foreignData = foreign
    }
  },

  watch: {
    data (data) {
      this.onSyncPersonal(data.personal)
    }
  }
}
</script>

<style lang="scss">
.personal-info-container {
  position: relative;
  & > .forms > .form {
    @include responsive (tablet, desktop) {
      margin-top: 10px;
      background: white;
      border-radius: 5px;
      box-shadow: 2px 8px 16px 0px rgba(0, 0, 0, 0.06);
    }

    @include responsive (xs-mobile, mobile) {
      & > .c-form-group {
        & > .form:first-child {
          padding-top: 15px;
          & > .c-title { display: none; }
        }
      }
    }
  }

}
</style>
